import React from "react";
import { Card } from "../../../components/shared/Card/Card";
import { useRequiredTenant } from "../../../contexts/TenantContext";
import { formatCurrency } from "../../../utils/formatters";

interface MarketAnalysisProps {
  propertyData: {
    estimatedValue: number;
    livingArea?: number;
    comps?: Array<{
      mlsSoldPrice?: number;
      squareFeet?: number;
      mlsDaysOnMarket?: number;
    }>;
  };
  className?: string;
}

export const MarketAnalysis: React.FC<MarketAnalysisProps> = ({
  propertyData,
  className = "",
}) => {
  const { colors } = useRequiredTenant().branding;

  // Calculate metrics
  const metrics = React.useMemo(() => {
    const defaultMetrics = {
      avgCompPricePerSqft: 0,
      avgDaysOnMarket: 0,
      totalComps: 0,
      recentSales: 0,
      priceRange: {
        min: 0,
        max: 0,
      },
    };

    try {
      const comps = propertyData.comps || [];
      const recentComps = comps.filter(
        (comp) => comp && typeof comp.mlsSoldPrice !== "undefined"
      );

      // Calculate average price per square foot
      let avgCompPricePerSqft = 0;
      const validPricePerSqft = recentComps
        .map((comp) => {
          const price = Number(comp.mlsSoldPrice);
          const sqft = Number(comp.squareFeet);
          return sqft > 0 ? price / sqft : null;
        })
        .filter((price): price is number => price !== null && !isNaN(price));

      if (validPricePerSqft.length > 0) {
        avgCompPricePerSqft =
          validPricePerSqft.reduce((a, b) => a + b, 0) /
          validPricePerSqft.length;
      }

      // Calculate average days on market
      const validDaysOnMarket = recentComps
        .map((comp) => Number(comp.mlsDaysOnMarket))
        .filter((days) => !isNaN(days) && days > 0);

      const avgDaysOnMarket =
        validDaysOnMarket.length > 0
          ? Math.round(
              validDaysOnMarket.reduce((a, b) => a + b, 0) /
                validDaysOnMarket.length
            )
          : 0;

      return {
        avgCompPricePerSqft,
        avgDaysOnMarket,
        totalComps: comps.length,
        recentSales: recentComps.length,
        priceRange: {
          min: propertyData.estimatedValue * 0.9,
          max: propertyData.estimatedValue * 1.1,
        },
      };
    } catch (error) {
      console.error("Error calculating metrics:", error);
      return defaultMetrics;
    }
  }, [propertyData.estimatedValue, propertyData.comps]);

  return (
    <Card title="Market Analysis" className={className} elevation="md">
      <div className="space-y-4">
        {/* Price per Square Foot */}
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Average Price/Sqft</span>
            <span className="font-medium">
              {formatCurrency(metrics.avgCompPricePerSqft)}
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${
                  propertyData.livingArea
                    ? Math.min(
                        100,
                        (propertyData.estimatedValue /
                          propertyData.livingArea /
                          metrics.avgCompPricePerSqft) *
                          100
                      )
                    : 0
                }%`,
                backgroundColor: colors.primary,
              }}
            />
          </div>
        </div>

        {/* Days on Market */}
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Days on Market</span>
            <span className="font-medium">{metrics.avgDaysOnMarket} days</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${Math.min(
                  100,
                  (metrics.avgDaysOnMarket / 90) * 100
                )}%`,
                backgroundColor: colors.accent,
              }}
            />
          </div>
        </div>

        {/* Price Range */}
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Price Range</span>
            <span className="font-medium">
              {formatCurrency(metrics.priceRange.min)} -{" "}
              {formatCurrency(metrics.priceRange.max)}
            </span>
          </div>
        </div>

        {/* Market Activity */}
        <div
          className="pt-2 mt-2 border-t"
          style={{ borderColor: colors.primary + "20" }}
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Total Comps</div>
              <div
                className="text-lg font-semibold"
                style={{ color: colors.primary }}
              >
                {metrics.totalComps}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Recent Sales</div>
              <div
                className="text-lg font-semibold"
                style={{ color: colors.primary }}
              >
                {metrics.recentSales}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MarketAnalysis;
